import { useQueryClient } from 'react-query';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useCustomContactFormSlidePanel } from '@frontend/create-contact-panel';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useMessagingFeaturesEnabled } from '@frontend/messaging-enabled-hook';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { dispatchTopBarTraySelectionEvent } from '@frontend/shared';
import { useNavigate, type AnyRoute, type AnySearch } from '@frontend/weave-router';
import { useAlert } from '@frontend/design-system';

export type QuickActions = {
  [key: string]: {
    label: string;
    icon: JSX.Element;
    onClick: () => void;
  };
};

export const useQuickActions = () => {
  const { selectedLocationIds } = useAppScopeStore();
  const { t } = useTranslation('contacts');
  const alerts = useAlert();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setShow } = useCustomContactFormSlidePanel();
  const { navigate: navigateSettings } = useSettingsNavigate();
  const { hasWSM } = SchedulerV3.Helpers.useCheckHasWSM();
  const messagingEnabled = useMessagingFeaturesEnabled();

  const actions: QuickActions = {
    ...(messagingEnabled && {
      'New Message': {
        label: t('New Message'),
        icon: <Icon name='messaging-small' />,
        onClick: () => navigate({ to: '/messages/$filter/new', params: { filter: 'inbox' } }),
      },
    }),
    'New Fax': {
      label: t('New Fax'),
      icon: <Icon name='fax-small' />,
      onClick: () => navigate({ to: '/fax/drafts/new' }),
    },
    'Payment History': {
      label: t('Payment History'),
      icon: <Icon name='history-small' />,
      onClick: () => navigate({ to: '/payments/invoices' }),
    },
    VMO: {
      label: t('VMO'),
      icon: <Icon name='voicemail-small' />,
      onClick: () => navigateSettings({ to: '/phone/overrides' }),
    },
    'New Patient': {
      label: t('New Patient'),
      icon: <Icon name='assign-user-small' />,
      onClick: () =>
        setShow({
          show: true,
          context: {
            mode: 'create',
            onSave: () => {
              alerts.success(t('Contact created successfully'));
              queryClient.invalidateQueries({ queryKey: [selectedLocationIds[0], 'contacts'] });
            },
            onError: () => {
              alerts.error(t('Contact creation error'));
            },
          },
        }),
    },
    'Phone Settings': {
      label: t('Phone Settings'),
      icon: <Icon name='settings-small' />,
      onClick: () => navigateSettings({ to: '/phone/main' }),
    },
    Waitlist: {
      label: t('Waitlist'),
      icon: <Icon name='quickfill-small' />,
      onClick: () => dispatchTopBarTraySelectionEvent('quickfill'),
    },
    'Appointment Hours': {
      label: 'Appointment Hours',
      icon: <Icon name='hourglass-small' />,
      onClick: () => navigateSettings({ to: '/schedule/appointment-hours' }),
    },
    'Phone Hours': {
      label: t('Phone Hours'),
      icon: <Icon name='hourglass-small' />,
      onClick: () =>
        navigateSettings({
          to: '/phone/phone-hours',
        }),
    },
    'Send Form': {
      label: t('Send Form'),
      icon: <Icon name='forms-small' />,
      onClick: () => navigate({ to: '/forms/library' as AnyRoute, search: { list: 'forms' } as AnySearch }),
    },
    ...(hasWSM && {
      'New Appointment': {
        label: t('New Appointment'),
        icon: <Icon name='calendar-add-small' />,
        onClick: () => SchedulerV3.triggerScheduleCustomEvent('schedule:new-appointment'),
      },
    }),
  };

  return actions;
};
